
import Vue from 'vue';
import {
  FileUploadBox,
  TimelineItem,
  Button,
  Badge,
  FileDownloadBox,
} from '@descomplica/vue-components';
import PostGradTCCDevelopmentFeedbackBox from './PostGradTCCDevelopmentFeedbackBox.vue';
import { ThesisDevelopmentItemType } from './types';

interface TCCDevelopmentSubmissionData {
  fileLoaded: boolean;
  fileName?: string;
  file?: string;
  error?: string;
}

export default Vue.extend({
  components: {
    TextButton: Button,
    TimelineItem,
    FileUploadBox,
    Badge,
    FileDownloadBox,
    PostGradTCCDevelopmentFeedbackBox,
  },
  props: {
    enabled: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String as () => ThesisDevelopmentItemType,
      required: true,
    },
    interactionsLeft: {
      type: Number,
      required: true,
    },
    link: {
      type: String,
      default: null,
    },
    feedbackText: {
      type: String,
      default: null,
    },
    feedbackLink: {
      type: String,
      default: null,
    },
    nextStepAvailable: {
      type: Boolean,
      default: false,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  data(): TCCDevelopmentSubmissionData {
    return {
      fileLoaded: false,
      fileName: '',
      error: '',
    };
  },
  computed: {
    title(): string {
      const submissionTypeToTitle: any = {
        [ThesisDevelopmentItemType.adjustmentsRequested]:
          'yourAssessments.tcc.tabs.thesisDevelopment.adjustmentsRequested.title',
        [ThesisDevelopmentItemType.firstSubmission]:
          'yourAssessments.tcc.tabs.thesisDevelopment.submission.title',
        [ThesisDevelopmentItemType.optionalSubmission]:
          'yourAssessments.tcc.tabs.thesisDevelopment.optionalSubmission.title',
      };
      return (this.$t(submissionTypeToTitle[this.type]) as string) || '';
    },
    subtitle(): string {
      return this.type === ThesisDevelopmentItemType.firstSubmission
        ? (this.$t('yourAssessments.tcc.tabs.thesisDevelopment.submission.instructions') as string)
        : '';
    },
    uploadLabel(): string {
      switch (this.type) {
        case ThesisDevelopmentItemType.adjustmentsRequested:
        case ThesisDevelopmentItemType.optionalSubmission:
          return this.$t(
            'yourAssessments.tcc.tabs.thesisDevelopment.adjustmentsRequested.fileLabel',
          ) as string;
        default:
          return this.$t(
            'yourAssessments.tcc.tabs.thesisDevelopment.submission.fileLabel',
          ) as string;
      }
    },
    linkFilename(): string {
      // This regex extracts the filename + extension from a URL (ignoring even the query params)
      const [match] = this.link.match(/[^/\\&\\?]+\.\w{3,4}(?=([\\?&].*$|$))/) ?? [];
      return decodeURI(match) ?? '';
    },
    submissionButtonLabel(): string {
      return (
        this.type === ThesisDevelopmentItemType.adjustmentsRequested
          ? this.$t(
              'yourAssessments.tcc.tabs.thesisDevelopment.adjustmentsRequested.sendAdjustments',
            )
          : this.$t('yourAssessments.tcc.tabs.thesisDevelopment.submission.sendFile')
      ) as string;
    },
    interactionsLeftModifier(): string {
      if (this.interactionsLeft === 3) return 'warning';
      return this.interactionsLeft > 3 ? 'safe' : 'danger';
    },
    submissionAvailable(): boolean {
      return this.enabled && !this.link && this.interactionsLeft > 0;
    },
  },
  methods: {
    resetFileState() {
      this.fileLoaded = false;
      this.fileName = '';
      this.error = '';
      this.file = undefined;
    },
    onFileLoad(file: File) {
      this.resetFileState();

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        this.file = reader.result as string;
        this.fileName = file.name;
        this.fileLoaded = true;
      });
      reader.readAsDataURL(file);
    },
    onFileError(errorCause: string) {
      this.resetFileState();

      if (errorCause === 'size') {
        this.error = this.$t(
          'yourAssessments.tcc.tabs.thesisDevelopment.submission.fileTooLong',
        ) as string;
      }
    },
    submitThesisDevelopment() {
      this.$emit('submitDevelopment', {
        file: this.file,
        fileName: this.fileName,
      });
    },
    openInNew(url: string) {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    goToNextTab() {
      this.$emit('goToNextTab');
    },
  },
});
